import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Edit2, ClipboardList, Copy } from 'lucide-react';
import { toast } from 'sonner';

const APPLIES_TO_OPTIONS = [
  { v: 'tenant_check_in', l: 'Tenant Check-In' },
  { v: 'tenant_check_out', l: 'Tenant Check-Out' },
  { v: 'pre_handover', l: 'Pre-Handover' },
  { v: 'post_handover', l: 'Post-Handover' },
  { v: 'snagging', l: 'Snagging' },
  { v: 'dlp', l: 'DLP' },
  { v: 'fitout_exit', l: 'Fit-Out Exit' },
  { v: 'periodic', l: 'Periodic' },
  { v: 'other', l: 'Other' },
];

const CHARGE_TO = [
  { v: 'na', l: 'N/A' },
  { v: 'tenant', l: 'Tenant' },
  { v: 'company', l: 'Company' },
  { v: 'shared', l: 'Shared' },
];

// A helpful starter for tenant check-out (damage + missing items).
const STARTER_ROWS = [
  { details: 'Front door keys', default_action: 'Return all keys', default_charged_to: 'tenant', default_cost: 0, remarks: '' },
  { details: 'Parking access card', default_action: 'Return card', default_charged_to: 'tenant', default_cost: 0, remarks: '' },
  { details: 'Building/lobby access fob', default_action: 'Return fob', default_charged_to: 'tenant', default_cost: 0, remarks: '' },
  { details: 'Mailbox key', default_action: 'Return', default_charged_to: 'tenant', default_cost: 0, remarks: '' },
  { details: 'Wall/paint damages', default_action: 'Repaint if damaged', default_charged_to: 'tenant', default_cost: 0, remarks: '' },
  { details: 'Kitchen appliances condition', default_action: 'Inspect & test', default_charged_to: 'na', default_cost: 0, remarks: '' },
  { details: 'Bathroom fittings', default_action: 'Check for leaks/breakage', default_charged_to: 'na', default_cost: 0, remarks: '' },
  { details: 'AC filters cleaned', default_action: 'Clean/replace filters', default_charged_to: 'tenant', default_cost: 0, remarks: '' },
  { details: 'General cleanliness', default_action: 'Deep clean if required', default_charged_to: 'tenant', default_cost: 0, remarks: '' },
  { details: 'Utility final readings', default_action: 'Record & settle', default_charged_to: 'tenant', default_cost: 0, remarks: '' },
];

const EMPTY_TEMPLATE = { name: '', description: '', applies_to: ['tenant_check_out'], rows: [], is_active: true };

export default function ChecklistTemplates() {
  const { user } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_TEMPLATE);
  const [saving, setSaving] = useState(false);

  const canWrite = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR'].includes(user?.role);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/checklist-templates`, { withCredentials: true });
      setTemplates(r.data || []);
    } catch (e) {
      toast.error('Failed to load templates');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY_TEMPLATE, rows: [] });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      name: t.name || '',
      description: t.description || '',
      applies_to: t.applies_to || [],
      rows: (t.rows || []).map(r => ({ ...r })),
      is_active: t.is_active !== false,
    });
    setOpen(true);
  };

  const addRow = () => {
    setForm({ ...form, rows: [...(form.rows || []), { details: '', default_action: '', default_charged_to: 'na', default_cost: 0, remarks: '' }] });
  };

  const insertStarter = () => {
    setForm({ ...form, rows: [...(form.rows || []), ...STARTER_ROWS.map(r => ({ ...r }))] });
    toast.success('Sample rows added');
  };

  const updateRow = (idx, patch) => {
    const rows = [...form.rows];
    rows[idx] = { ...rows[idx], ...patch };
    setForm({ ...form, rows });
  };

  const removeRow = (idx) => {
    const rows = [...form.rows];
    rows.splice(idx, 1);
    setForm({ ...form, rows });
  };

  const toggleAppliesTo = (v) => {
    const cur = form.applies_to || [];
    if (cur.includes(v)) {
      setForm({ ...form, applies_to: cur.filter(x => x !== v) });
    } else {
      setForm({ ...form, applies_to: [...cur, v] });
    }
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/checklist-templates/${editingId}`, form, { withCredentials: true });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/checklist-templates`, form, { withCredentials: true });
        toast.success('Template created');
      }
      setOpen(false);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/checklist-templates/${t.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchTemplates();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const duplicate = async (t) => {
    try {
      const payload = {
        name: `${t.name} (Copy)`,
        description: t.description,
        applies_to: t.applies_to || [],
        rows: (t.rows || []).map(r => ({ ...r })),
        is_active: true,
      };
      await axios.post(`${API}/checklist-templates`, payload, { withCredentials: true });
      toast.success('Duplicated');
      fetchTemplates();
    } catch (e) {
      toast.error('Duplicate failed');
    }
  };

  return (
    <Layout>
      <div data-testid="checklist-templates-page" className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground dark:text-slate-100 flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-amber-500" /> Checklist Templates
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Build reusable checklists for tenant check-in / check-out and any inspection. Rows include Details, Findings,
              Action Required, Cost, Charged To (Tenant / Company), and Remarks — fully customisable per template.
            </p>
          </div>
          {canWrite && (
            <Button data-testid="new-template-btn" onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              <Plus className="w-4 h-4 mr-1" /> New Template
            </Button>
          )}
        </div>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Loading…</div>
        ) : templates.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground">
            <ClipboardList className="w-10 h-10 mx-auto opacity-40 mb-2" />
            <div className="font-semibold">No checklist templates yet</div>
            <div className="text-sm">Create your first template — e.g. &ldquo;Tenant Check-Out (Standard)&rdquo;.</div>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {templates.map(t => (
              <Card key={t.id} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-lg truncate" data-testid={`tpl-name-${t.id}`}>{t.name}</div>
                    {t.description && <div className="text-sm text-muted-foreground mt-0.5">{t.description}</div>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(t.applies_to || []).map(a => (
                        <span key={a} className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 capitalize">
                          {a.replace(/_/g, ' ')}
                        </span>
                      ))}
                      {(!t.applies_to || t.applies_to.length === 0) && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Any inspection</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">{(t.rows || []).length} rows</div>
                  </div>
                  {canWrite && (
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`tpl-edit-${t.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => duplicate(t)} data-testid={`tpl-duplicate-${t.id}`}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(t)} data-testid={`tpl-delete-${t.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="template-dialog" className="max-w-4xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Template' : 'New Checklist Template'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Template Name *</Label>
                  <Input data-testid="tpl-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Tenant Check-Out (Standard)" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" />
                </div>
              </div>

              <div>
                <Label className="mb-1 block">Applies To (inspection types)</Label>
                <div className="flex flex-wrap gap-2">
                  {APPLIES_TO_OPTIONS.map(opt => {
                    const on = (form.applies_to || []).includes(opt.v);
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => toggleAppliesTo(opt.v)}
                        className={`text-xs px-3 py-1 rounded-full border transition ${on ? 'bg-amber-500 border-amber-500 text-slate-900 font-semibold' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                      >
                        {opt.l}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Checklist Rows</Label>
                <div className="flex gap-2">
                  <Button size="sm" type="button" variant="outline" onClick={insertStarter} data-testid="tpl-starter-btn">Insert sample rows</Button>
                  <Button size="sm" type="button" onClick={addRow} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="tpl-add-row-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Row
                  </Button>
                </div>
              </div>

              {(form.rows || []).length === 0 ? (
                <div className="border border-dashed rounded-lg py-8 text-center text-sm text-muted-foreground">
                  No rows yet. Click <b>Add Row</b> or <b>Insert sample rows</b> to get started.
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-2 py-2 w-10">S.No</th>
                        <th className="px-2 py-2 text-left">Details</th>
                        <th className="px-2 py-2 text-left">Action Required</th>
                        <th className="px-2 py-2 text-left w-32">Charged To</th>
                        <th className="px-2 py-2 text-right w-24">Cost</th>
                        <th className="px-2 py-2 text-left">Remarks</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.rows.map((r, idx) => (
                        <tr key={idx} className="border-t align-top">
                          <td className="px-2 py-1.5 text-center text-muted-foreground text-xs">{idx + 1}</td>
                          <td className="px-2 py-1.5">
                            <Input data-testid={`tpl-row-details-${idx}`} value={r.details || ''} onChange={(e) => updateRow(idx, { details: e.target.value })} placeholder="e.g. Front door keys" />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input value={r.default_action || ''} onChange={(e) => updateRow(idx, { default_action: e.target.value })} placeholder="e.g. Return all keys" />
                          </td>
                          <td className="px-2 py-1.5">
                            <Select value={r.default_charged_to || 'na'} onValueChange={(v) => updateRow(idx, { default_charged_to: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {CHARGE_TO.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="number" min="0" step="0.01" className="text-right" value={r.default_cost ?? ''} onChange={(e) => updateRow(idx, { default_cost: e.target.value === '' ? null : parseFloat(e.target.value) })} placeholder="0" />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input value={r.remarks || ''} onChange={(e) => updateRow(idx, { remarks: e.target.value })} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Button size="sm" variant="ghost" onClick={() => removeRow(idx)}>
                              <Trash2 className="w-4 h-4 text-rose-600" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="tpl-save-btn" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                {saving ? 'Saving…' : (editingId ? 'Update Template' : 'Create Template')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
